import { CurecallModalContainer } from '@honestica/curecall-ui'
import MedicalTeamComment from './medical-team-comments.component'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { TMedicalTeamComment } from './medical-team-comments.type'
import { ErrorQueryHandler } from '../error/error-query-handler.component'
import { PatientService } from 'src/services/patient.service'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { TPatientFollowup } from 'src/types/followup'

export type TMedicalTeamCommentsModalProps = {
  isOpen: boolean
  onToggle: (event?: React.MouseEvent<HTMLElement>) => void
  patientId: number
  patientFollowup: Pick<TPatientFollowup, 'id' | 'endReason'>
}

const MedicalTeamCommentsModal = ({
  patientId,
  isOpen,
  onToggle,
  patientFollowup,
}: TMedicalTeamCommentsModalProps) => {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const queryClient = useQueryClient()
  const {
    isLoading,
    error,
    data: medicalTeamComments,
  } = useQuery<TMedicalTeamComment[]>({
    queryKey: ['patient-medical-team-comments', patientId],
    queryFn: () => {
      return PatientService.getPatientComments(project.id, patientId)
    },
    staleTime: 60 * 1000, // 1 minute
  })

  if (isLoading || !medicalTeamComments) return <div className="loading" />
  if (error) return <ErrorQueryHandler error={error} />

  const onUpdateMedicalTeamComment = (
    patientId: number,
    updatedMedicalTeamCommentList: TMedicalTeamComment[],
  ) => {
    queryClient.setQueryData(
      ['patient-medical-team-comments', patientId],
      () => updatedMedicalTeamCommentList,
    )
    queryClient
      .getQueriesData({ queryKey: ['planning-patient-med-events'] })
      .forEach(async ([queryKey, queryData]: any) => {
        const planningPatientMedEvents = queryData?.patientMedEvents || []
        const hasUpdatedPatient = planningPatientMedEvents.some((planningPatientMedEvent: any) => {
          return planningPatientMedEvent.Patient.id === patientId
        })
        if (hasUpdatedPatient) {
          await queryClient.invalidateQueries(queryKey)
        }
      })
  }

  return (
    <CurecallModalContainer
      closeModalAriaLabel={t('components.medical-team-comments.close')}
      title={t('components.medical-team-comments.title')}
      isOpen={isOpen}
      toggle={onToggle}
      dialogClassName="c-w-[900px] "
    >
      <MedicalTeamComment
        patientId={patientId}
        medicalTeamComments={medicalTeamComments}
        onUpdateMedicalTeamComment={onUpdateMedicalTeamComment}
        patientFollowupId={patientFollowup.id}
        isPatientFollowupEnded={!!patientFollowup.endReason}
      />
    </CurecallModalContainer>
  )
}

export default MedicalTeamCommentsModal
